import Box from "@mui/material/Box";
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTSPSurveyPartsBySupplier } from "../../services/tps.service";
import { TPS_RESPONSE_STATUSES, COO_TOOLTIP_DUAL_SOURCE, COO_COUNTRY_OVERRIDE_CONFIRMATION, TPSMEX_ORIGIN_CRITERION_OPTIONS, ORIGIN_CRITERION_COMMENT_DEPS } from "../../utils/surveyLiterals";
import { CircleProgressIndicator } from "../CircleProgressIndicator";
import { getCountry } from "../../services/external.service";
import { ReactComponent as AddIcon } from "@deere/ux.brand-foundations/icons/add_circle.svg";
import { IconButton, Typography, FormControl, FormLabel, SelectField, Option, Tooltip, Button } from "@deere/fuel-react";
import ModalAddCountry from "../coo/ModalAddCountry";
import { ModalGeneric } from "../ModalGeneric";
import useTpsContext from "../../hooks/useTpsContext";
import { Popover, OverlayTrigger } from "@deere/ux.uxframe-react";
import { SVGIcon } from "../SVGIcon";
import { ModalComments } from '../modals/ModalComments';

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

export const PartsTableTPSMEX = ({ campaign, partsInResponse, selectionModel, setSelectionModel, editedPartCountries, setEditedPartCountries, survey, setIsSubmitted }) => {
  const { suppliers, updatePartsArray, tpsResponseSelected } = useTpsContext();
  const [data, setData] = useState(undefined);
  const [countryList, setCountryList] = useState([]);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [rowToUpdateCountry, setRowToUpdateCountry] = useState(null);
  const [rowsToDisable, setRowsToDisable] = useState([]);
  const [countryDropDownValue, setCountryDropDownValue] = useState("");
  const [originCriterion, setOriginCriterion] = useState("");
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [idForCommentsPopup, setIdforCommentsPopup] = useState("");
  const { authState } = useOktaAuth();
  const { t } = useTranslation();
  const columns = useMemo(() => [
    { field: "supplierNumber", headerName: t("parts_tbl_column_header_1"), width: 120 },
    { field: "deerePartNumber", headerName: t("parts_tbl_column_header_2"), width: 120 },
    { field: "deerePartDesc", headerName: t("parts_tbl_column_header_3"), width: 130 },
    { field: "hsCode", headerName: t("parts_tbl_column_header_4"), width: 120 },
    { field: "originCriterion", headerName: t("parts_tbl_column_header_7"), width: 100, renderCell: (params) => renderOriginCriterion(params) },
    { field: "country", headerName: t("parts_tbl_column_header_6"), width: 130, renderCell: (params) => renderCountryCell(params) },
    { field: "status", headerName: t("parts_tbl_column_header_5"), width: 100 },
  ]);
  const style = {
    width: "100%",
    "& .disable-row-status": { color: "#b3b3b3" },
  };

  useEffect(() => {
    if (tpsResponseSelected && tpsResponseSelected.id && partsInResponse && partsInResponse.length > 0) {
      let tempArr = [];
      partsInResponse.forEach((part) => {
        let index = tempArr.findIndex((d) => d.id === part.id);
        if (index === -1) {
          tempArr.push({
            id: part.id,
            country: [part.country],
            originCriterion: part.originCriterion,
            comments: part.comments,
            supplierNumber: part.supplierNumber,
            deerePartNumber: part.deerePartNumber,
            deerePartDesc: part.deerePartDesc,
          });
        } else tempArr[index].country = [...tempArr[index].country, part.country];
      });
      setEditedPartCountries(tempArr);
    }
  }, [tpsResponseSelected]);

  useEffect(() => {
    getCountry(authState.accessToken.accessToken)
      .then((res) => {
        if (res.length > 0) {
          res.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
          setCountryList(res);
        }
      })
      .catch((err) => {
        console.log(err);
        setCountryList([]);
      });
  }, []);

  useEffect(() => {
    if (Array.isArray(suppliers) && suppliers.length > 0) {
      getTSPSurveyPartsBySupplier(suppliers, campaign.id, authState.accessToken.accessToken, survey.code)
        .then((response) => {
          if (Array.isArray(response)) {
            response.sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0));
            let tempArr = [];
            let tempArrDisable = [];
            const responseActive = [],
              responseDisabled = [];
            response.forEach((res) => {
              let editedPartCountryFound = editedPartCountries.find((d) => d.id === res.id);
              if (editedPartCountryFound) {
                res.country = editedPartCountryFound.country; //Retaining the country change done by user via popup/drop-down
                res.strCountry = editedPartCountryFound.country.join(", "); //Adding this property for display purpose
              } else {
                res.strCountry = res.country.join(", "); //Adding this property for display purpose
              }
              //**Logic explanation at bottom
              if (res.status.trim().toUpperCase() === TPS_RESPONSE_STATUSES.SUBMITTED.toUpperCase() || res.status.trim().toUpperCase() === TPS_RESPONSE_STATUSES.IN_PROCESS.toUpperCase()) {
                if (tpsResponseSelected && tpsResponseSelected.id) {
                  let index = partsInResponse.findIndex((part) => part.id === res.id);
                  if (index === -1) {
                    tempArrDisable.push(res.id);
                    responseDisabled.push(res);
                  } else {
                    tempArr.push({
                      id: res.id,
                      country: res.country,
                      originCriterion: res.originCriterion,
                      comments: res.comments,
                      supplierNumber: res.supplierNumber,
                      deerePartNumber: res.deerePartNumber,
                      deerePartDesc: res.deerePartDesc,
                    });
                    responseActive.push(res);
                  }
                } else {
                  tempArrDisable.push(res.id);
                  responseDisabled.push(res);
                }
              } else {
                responseActive.push(res);
              }
            });
            setRowsToDisable(tempArrDisable);
            setData([...responseActive, ...responseDisabled]); // This will sort the disabled parts at the bottom & keep active ones at the top.
            updatePartsArray(tempArr);
          }
        })
        .catch((error) => {
          throw error;
        });
    } else {
      setData(undefined);
    }
  }, [authState.accessToken.accessToken, campaign.id, suppliers, tpsResponseSelected]);

  const countryChangeDropdownHandler = (value, isConfirmationReceived) => {
    setCountryDropDownValue(value);
    let isConfirmationNeeded = false;
    if (!isConfirmationReceived) {
      for (let i = 0; i < selectionModel.length; i++) {
        let selectedData = data.find((d) => d.id === selectionModel[i]);
        if (selectedData.country && selectedData.country.length > 0) {
          isConfirmationNeeded = true;
          break;
        }
      }
    }
    if (isConfirmationNeeded) setShowModal(true);
    else {
      setData((prevState) => {
        prevState.forEach((d) => {
          let index = selectionModel.findIndex((sel) => sel === d.id);
          if (index > -1) d.country = d.strCountry = [value];
        });
        return [...prevState];
      });
      let tempArr = [...editedPartCountries];
      selectionModel.forEach((sel) => {
        let index = tempArr.findIndex((d) => d.id === sel);
        const partData = data.filter((i) => i.id === sel)[0];
        index > -1
          ? (tempArr[index].country = [value])
          : tempArr.push({
              id: sel,
              country: [value],
              originCriterion: partData.originCriterion,
              comments: partData.comments,
              supplierNumber: partData.supplierNumber,
              deerePartNumber: partData.deerePartNumber,
              deerePartDesc: partData.deerePartDesc,
            });
      });
      setEditedPartCountries(tempArr);
      updatePartsArray(tempArr);
      setShowModal(false);
      setCountryDropDownValue("");
      setIsSubmitted(false);
    }
  };

  const countryChangePopupHandler = (id, arrCountries) => {
    setData((prevState) => {
      prevState.forEach((d) => {
        if (d.id === id) {
          d.country = arrCountries;
          d.strCountry = arrCountries.join(", ");
        }
      });
      return [...prevState];
    });
    let tempArr = [...editedPartCountries];
    let found = false;
    tempArr.forEach((d) => {
      if (d.id === id) {
        found = true;
        d.country = arrCountries;
      }
    });
    if (!found) {
      const partData = data.filter((i) => i.id === id)[0];
      tempArr.push({
        id: id,
        country: arrCountries,
        originCriterion: partData.originCriterion,
        comments: partData.comments,
        supplierNumber: partData.supplierNumber,
        deerePartNumber: partData.deerePartNumber,
        deerePartDesc: partData.deerePartDesc,
      });
    }
    setEditedPartCountries(tempArr);
    updatePartsArray(tempArr);
    setIsSubmitted(false);
  };

  const originCriterionHandler = (value) => {
    setData((prevState) => {
      prevState.forEach((d) => {
        let index = selectionModel.findIndex((sel) => sel === d.id);
        if (index > -1) {
          d.originCriterion = value;
          if (!ORIGIN_CRITERION_COMMENT_DEPS.includes(value)) d.comments = "";
        }
      });
      return [...prevState];
    });
    let tempArr = [...editedPartCountries];
    selectionModel.forEach((sel) => {
      let index = tempArr.findIndex((d) => d.id === sel);
      const partData = data.filter((i) => i.id === sel)[0];
      index > -1
        ? (tempArr[index].originCriterion = value)
        : tempArr.push({
            id: sel,
            country: partData.country,
            originCriterion: value,
            comments: !ORIGIN_CRITERION_COMMENT_DEPS.includes(value) ? "" : partData.comments,
            supplierNumber: partData.supplierNumber,
            deerePartNumber: partData.deerePartNumber,
            deerePartDesc: partData.deerePartDesc,
          });
    });
    setEditedPartCountries(tempArr);
    updatePartsArray(tempArr);
    setShowModal(false);
    setOriginCriterion("");
    setIsSubmitted(false);
  };

  const commentsHandler = (value) => {
    setData((prevState) => {
      let index = prevState.findIndex((d) => d.id === idForCommentsPopup);
      if (index > -1) prevState[index].comments = value;
      return [...prevState];
    });
    let tempArr = [...editedPartCountries];
    let index = tempArr.findIndex((d) => d.id === idForCommentsPopup); 
    const partData = data.filter((i) => i.id === idForCommentsPopup)[0];
    index > -1
      ? (tempArr[index].comments = value)
      : tempArr.push({
          id: idForCommentsPopup,
          country: partData.country,
          originCriterion: partData.originCriterion,
          comments: value,
          supplierNumber: partData.supplierNumber,
          deerePartNumber: partData.deerePartNumber,
          deerePartDesc: partData.deerePartDesc,
        });
    setIdforCommentsPopup("");
    setEditedPartCountries(tempArr);
    updatePartsArray(tempArr);
    setShowModal(false);
    setIsSubmitted(false);
  }

  const disableRow = (id) => {
    let index = rowsToDisable.findIndex((rowId) => rowId === id);
    if (index === -1) return true;
    return false;
  };

  const setDisableRowStyle = (id) => {
    let index = rowsToDisable.findIndex((rowId) => rowId === id);
    if (index > -1) return "disable-row-status";
    return "";
  };

  const checkDisable = (id) => {
    let index = rowsToDisable.findIndex((rowId) => rowId === id);
    if (index > -1) return true;
    else return false;
  };

  const renderCountryCell = (params) => {
    return (
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <span>{params.row.strCountry}</span>
        <Tooltip title={COO_TOOLTIP_DUAL_SOURCE} placement="top">
          <IconButton
            onClick={() => {
              setRowToUpdateCountry(params.row);
              setShowCountryModal(true);
            }}
            disabled={checkDisable(params.row.id)}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  const renderOriginCriterion = (params) => {
    return (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <div>{params.value}</div>
        {ORIGIN_CRITERION_COMMENT_DEPS.includes(params.row.originCriterion) && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {params.row.comments && params.row.comments !== "" && (
              <OverlayTrigger
                trigger="click"
                overlay={
                  <Popover id="popover-example">
                    <Popover.Content>{params.row.comments}</Popover.Content>
                  </Popover>
                }
              >
                <IconButton variant="uxf-icon" className="btn-uxf-icon-no-hover" disabled={checkDisable(params.row.id)}>
                  <SVGIcon />
                </IconButton>
              </OverlayTrigger>
            )}
            <IconButton
              variant="uxf-icon"
              className="btn-uxf-icon-no-hover"
              disabled={checkDisable(params.row.id)}
              onClick={() => {
                setIdforCommentsPopup(params.row.id);
                setShowCommentsModal(true);
              }}
              style={{ marginLeft: "0px" }}
            >
              <AddIcon />
            </IconButton>
          </div>
        )}
      </div>
    );
  };

  const dTable = (
    <Box sx={style}>
      <DataGrid
        rows={data}
        columns={columns}
        autoHeight
        pageSize={5}
        checkboxSelection
        disableSelectionOnClick
        disableColumnMenu
        isRowSelectable={(params) => disableRow(params.row.id)}
        getRowClassName={(params) => setDisableRowStyle(params.row.id)}
        onSelectionModelChange={(ids) => setSelectionModel(ids)}
        components={{ Toolbar: CustomToolbar }}
        selectionModel={selectionModel}
        sx={{
          "& .MuiDataGrid-cell": { whiteSpace: "normal!important" },
          "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal!important", lineHeight: 1.5 },
        }}
        getRowHeight={() => "auto"}
      />
    </Box>
  );

  return Array.isArray(data) ? (
    <>
      <Typography size="md" component="p" gutterBottom fontWeight="semibold" paddingLeft={2} marginTop={2}>
        {t("parts_section_lbl_1")}
      </Typography>
      <Typography size="sm" component="p" gutterBottom fontWeight="regular" paddingLeft={2}>
        {t("parts_section_sub_lbl_1")}
      </Typography>
      {dTable}
      <FormControl variant="standard" sx={{ paddingLeft: "8px" }}>
        <FormLabel>
          <Typography size="md" component="p" gutterBottom fontWeight="semibold" marginTop={2}>
            {t("parts_section_lbl_2")}
          </Typography>
        </FormLabel>
        <SelectField
          defaultValue=""
          disabled={selectionModel.length === 0}
          onChange={(e) => {
            countryChangeDropdownHandler(e.target.value, false);
          }}
          value={countryDropDownValue}
        >
          <Option disabled hidden value="">
            {t("lbl_dropdown_select")}
          </Option>
          {countryList.map((opt) => (
            <Option key={opt.iso2CountryCode} value={opt.name}>
              {opt.name}
            </Option>
          ))}
        </SelectField>
        <FormLabel>
          <Typography size="md" component="p" gutterBottom fontWeight="semibold" marginTop={6}>
            {t("parts_section_lbl_3")}
          </Typography>
        </FormLabel>
        <SelectField
          defaultValue=""
          disabled={selectionModel.length === 0}
          onChange={(e) => {
            originCriterionHandler(e.target.value);
          }}
          value={originCriterion}
        >
          <Option disabled hidden value="">
            {t("lbl_dropdown_select")}
          </Option>
          {TPSMEX_ORIGIN_CRITERION_OPTIONS.map((i) => (
            <Option key={i} value={i}>
              {i}
            </Option>
          ))}
        </SelectField>
      </FormControl>
      {showCountryModal && (
        <ModalAddCountry showModal={showCountryModal} setShowModal={setShowCountryModal} rowToUpdateCountry={rowToUpdateCountry} countryList={countryList} saveHandler={countryChangePopupHandler} />
      )}
      <ModalGeneric
        showModal={showModal}
        setShowModal={setShowModal}
        labelBtnPrimary={t("confirm_label")}
        labelBtnSecondary={t("btn_cancel")}
        msgModal={COO_COUNTRY_OVERRIDE_CONFIRMATION}
        titleModal={t("confirm_label")}
        onClose={() => {
          setShowModal(false);
          setCountryDropDownValue("");
        }}
        handleSubmit={() => {
          countryChangeDropdownHandler(countryDropDownValue, true);
        }}
      />
      <ModalComments
        showModal={showCommentsModal}
        setShowModal={setShowCommentsModal}
        handleSubmit={commentsHandler}
        idForCommentsPopup={idForCommentsPopup}
        setIdforCommentsPopup={setIdforCommentsPopup}
        data={data}
        labelBtnPrimary={t("btn_review_submit")}
        labelBtnSecondary={t("btn_cancel")}
        titleModal={t("lbl_Comments")}
      />
    </>
  ) : (
    <CircleProgressIndicator title={t("tps_lbl_waiting_country_message_title")} subtitle={t("tps_lbl_waiting_country_message_subtitle")} />
  );
};

//**
// Logic for Row Disable-
// if the Status is SUBMITTED or IN PROCESS
//    - check if tpsResponseSelected.id is available
//      - If available that means a response is selected & now we can check if the part is present in the 'partsInResponse' array.
//        - If present, that means this part is tied to the response currently selected, so it can still be edited.
//        - If not present, that means this part is tied to a different response, so cannot be edited here.
//      - If not available meaning a response is not selected and a new response is going to be created,
//        so all the IN_PROCESS/SUBMITTED parts should not be allowed to be edited since they all are tied to some different responses.
